import React, { useState, useEffect, useRef } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import axios from 'axios';
import { Phone, PhoneOff, Loader2 } from 'lucide-react';

// Create API instance without credentials for public embed access
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

const EmbedLiveAvatarMinimalPageContent = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarVideoTrack, setAvatarVideoTrack] = useState(null);
  const [viewMode, setViewMode] = useState('full');
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]);
  const [dynamicVariableValues, setDynamicVariableValues] = useState({});

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const fpsIntervalRef = useRef(null);
  const videoAttachedRef = useRef(false);
  const avatarReadyNotifiedRef = useRef(false);

  // Detect view mode from query string and extract dynamic variable names
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('view');
    if (mode === 'dynamic') {
      setViewMode('dynamic');
      const systemParams = ['view', 'token', 'embed_token', 'key'];
      const variableNames = [];
      const initialValues = {};
      params.forEach((value, key) => {
        if (!systemParams.includes(key)) {
          variableNames.push(key);
          if (value && !value.startsWith('{{') && !value.endsWith('}}')) {
            initialValues[key] = value;
          }
        }
      });
      setDynamicVariableNames(variableNames);
      setDynamicVariableValues(initialValues);
    }
  }, []);

  useEffect(() => {
    // Component mounted
  }, [agentId]);

  // Load agent details
  useEffect(() => {
    const loadAgent = async () => {
      try {
        const response = await api.get(`/agents/${agentId}`);
        
        if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          setError('Received unexpected response format. Please check your API configuration.');
          setLoading(false);
          return;
        }
        
        setAgent(response.data);
        
        const liveavatarEnabledRaw = response.data.liveavatar_enabled;
        const liveavatarEnabled = liveavatarEnabledRaw === true || 
                                 liveavatarEnabledRaw === 'true' || 
                                 liveavatarEnabledRaw === 1 ||
                                 liveavatarEnabledRaw === '1';
        const avatarId = response.data.liveavatar_avatar_id;
        const hasAvatarId = avatarId && typeof avatarId === 'string' && avatarId.trim() !== '';
        
        if (!liveavatarEnabled) {
          setError(`LiveAvatar is not enabled for this agent.`);
        } else if (!hasAvatarId) {
          setError(`Avatar ID is missing.`);
        }
      } catch (err) {
        setError(`Agent not found: ${err.response?.data?.detail || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (agentId) {
      loadAgent();
    } else {
      setLoading(false);
    }
  }, [agentId]);

  const updateVariableValue = (name, value) => {
    if (!name) return;
    setDynamicVariableValues(prev => ({ ...prev, [name]: value }));
  };

  const getVariableLabel = (varName) => {
    if (!varName) return '';
    return varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Start LiveAvatar session
  const startLiveAvatarSession = async () => {
    if (!agent) return;

    try {
      setIsConnecting(true);
      setError(null);

      const timestamp = Date.now();
      const roomName = `test-agent-${agent.id}-${timestamp}`;

      const variablesToSend = {};
      dynamicVariableNames.forEach(name => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) {
          variablesToSend[name] = value.trim();
        }
      });

      const sessionPayload = {
        room_name: roomName,
        agent_id: agent.id,
        channel: 'test',
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend })
      };

      const sessionResponse = await api.post('/sessions/test', sessionPayload);
      const testSessionId = sessionResponse.data.session_id;
      setSessionId(testSessionId);
      
      const tokenResponse = await api.post('/token', {
        room: roomName,
        identity: 'liveavatar-user'
      });
      
      const { url, token } = tokenResponse.data;
      
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: false, // Keep call active when user switches browser tabs
        videoCaptureDefaults: {
          resolution: { width: 640, height: 360 }
        },
        defaultAudioCaptureOptions: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      const logAvatarVideoResolution = (publication, track, label = 'Avatar video') => {
        const fromPub = publication?.dimensions ? `${publication.dimensions.width}x${publication.dimensions.height}` : null;
        const fromTrack = track?.dimensions ? `${track.dimensions.width}x${track.dimensions.height}` : null;
        const q = publication?.videoQuality;
        const reqLabel = q === VideoQuality.LOW ? 'LOW (360p)' : q === VideoQuality.MEDIUM ? 'MEDIUM' : q === VideoQuality.HIGH ? 'HIGH (720p)' : 'unknown';
        console.log(`[LiveAvatar] ${label} — requested: ${reqLabel} | FPS: 30 | publication: ${fromPub || 'n/a'} | track: ${fromTrack || 'n/a'}`);
        const logPlayback = () => {
          const el = videoRef.current;
          const w = el?.videoWidth ?? 0;
          const h = el?.videoHeight ?? 0;
          if (w && h) {
            const is720 = w >= 1280 || h >= 720;
            const is360 = w <= 640 && h <= 360;
            console.log(`[LiveAvatar] ${label} — actual playback: ${w}x${h} → ${is720 ? '720p' : is360 ? '360p' : 'other'}`);
            if (typeof el.requestVideoFrameCallback === 'function') {
              if (fpsIntervalRef.current) {
                clearInterval(fpsIntervalRef.current);
                fpsIntervalRef.current = null;
              }
              const measureAndLogFps = () => {
                const videoEl = videoRef.current;
                if (!videoEl?.videoWidth || typeof videoEl.requestVideoFrameCallback !== 'function') return;
                let frameCount = 0;
                const start = performance.now();
                const countFrame = () => {
                  frameCount++;
                  if (performance.now() - start < 1100) {
                    videoEl.requestVideoFrameCallback(countFrame);
                  } else {
                    const fps = Math.round((frameCount * 1000) / (performance.now() - start));
                    console.log(`[LiveAvatar] ${label} — FPS: ~${fps}`);
                  }
                };
                videoEl.requestVideoFrameCallback(countFrame);
              };
              measureAndLogFps();
              fpsIntervalRef.current = setInterval(measureAndLogFps, 2000);
            } else {
              console.log(`[LiveAvatar] ${label} — actual FPS: (not measurable in this browser)`);
            }
          } else {
            console.log(`[LiveAvatar] ${label} — actual playback: ${w}x${h} (video not ready yet; may need user interaction to play)`);
          }
        };
        setTimeout(logPlayback, 800);
        setTimeout(logPlayback, 2500);
      };

      const attachVideoTrack = (track, publication) => {
        if (!track || !videoRef.current) return false;
        if (videoAttachedRef.current) return true;
        
        try {
          track.attach(videoRef.current);
          
          if (!videoRef.current.srcObject && track.mediaStreamTrack) {
            try {
              videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
            } catch {}
          }
          
          videoAttachedRef.current = true;
          
          setTimeout(() => {
            if (videoRef.current?.srcObject) {
              videoRef.current.play().catch(() => {});
            }
          }, 300);
          
          return true;
        } catch (error) {
          return false;
        }
      };
      
      const subscribeToAvatarVideo = (participant) => {
        const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                        participant.name === 'liveavatar-avatar-agent' ||
                        (participant.identity && participant.identity.includes('liveavatar')) ||
                        (participant.name && participant.name.includes('liveavatar'));
        
        if (isAvatar) {
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track) {
              try {
                publication.setVideoQuality(VideoQuality.LOW);
                publication.setVideoFPS(30);
              } catch (_) {}
              setAvatarVideoTrack(publication.track);
              attachVideoTrack(publication.track, publication);
              logAvatarVideoResolution(publication, publication.track, 'Avatar video (existing)');
            }
          });
        }
      };
      
      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
        
        newRoom.remoteParticipants.forEach((participant) => {
          subscribeToAvatarVideo(participant);
        });
      });
      
      newRoom.on(RoomEvent.Disconnected, () => {
        if (fpsIntervalRef.current) {
          clearInterval(fpsIntervalRef.current);
          fpsIntervalRef.current = null;
        }
        videoAttachedRef.current = false;
        avatarReadyNotifiedRef.current = false;
        setIsConnected(false);
        setAvatarVideoTrack(null);
      });
      
      newRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        subscribeToAvatarVideo(participant);
      });
      
      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            try {
              publication.setVideoQuality(VideoQuality.LOW);
              publication.setVideoFPS(30);
            } catch (_) {}
            setAvatarVideoTrack(track);
            if (!attachVideoTrack(track, publication)) {
              setTimeout(() => {
                if (videoRef.current && track) {
                  attachVideoTrack(track, publication);
                }
              }, 500);
            }
            logAvatarVideoResolution(publication, track, 'Avatar video (TrackSubscribed)');
          }
        } else if (track.kind === 'audio') {
          if (audioRef.current) {
            track.attach(audioRef.current);
          }
        }
      });
      
      newRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
        if (publication.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            // LiveKit will auto-subscribe
          }
        }
      });
      
      newRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        if (track.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            videoAttachedRef.current = false;
            avatarReadyNotifiedRef.current = false;
            setAvatarVideoTrack(null);
            if (videoRef.current) {
              track.detach(videoRef.current);
            }
          }
        }
      });
      
      await newRoom.connect(url, token);
      setRoom(newRoom);
      
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to start call';
      setError(detail);
      setIsConnecting(false);
    }
  };

  // Stop LiveAvatar session
  const stopLiveAvatarSession = async () => {
    try {
      if (room) {
        await room.disconnect();
        setRoom(null);
      }
      
      if (sessionId) {
        await api.post(`/sessions/${sessionId}/end`);
      }
      
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setSessionId(null);
      setAvatarVideoTrack(null);
      
    } catch (err) {
      // Silently handle stop errors
    }
  };
  
  // Fallback poll for avatar video (events are primary, this catches edge cases)
  useEffect(() => {
    if (!room || !isConnected) return;
    
    const checkForAvatarVideo = () => {
      if (videoAttachedRef.current) return;
      
      room.remoteParticipants.forEach((participant) => {
        const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                        participant.name === 'liveavatar-avatar-agent' ||
                        (participant.identity && participant.identity.includes('liveavatar')) ||
                        (participant.name && participant.name.includes('liveavatar'));
        
        if (isAvatar && !videoAttachedRef.current) {
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track && !videoAttachedRef.current) {
              try {
                publication.setVideoQuality(VideoQuality.LOW);
                publication.setVideoFPS(30);
              } catch (_) {}
              setAvatarVideoTrack(publication.track);
              if (videoRef.current) {
                try {
                  publication.track.attach(videoRef.current);
                  if (!videoRef.current.srcObject && publication.track.mediaStreamTrack) {
                    videoRef.current.srcObject = new MediaStream([publication.track.mediaStreamTrack]);
                  }
                  videoAttachedRef.current = true;
                  videoRef.current.play().catch(() => {});
                } catch {}
              }
            }
          });
        }
      });
    };
    
    checkForAvatarVideo();
    const interval = setInterval(checkForAvatarVideo, 500);
    return () => clearInterval(interval);
  }, [room, isConnected]);
  
  useEffect(() => {
    if (avatarVideoTrack && videoRef.current) {
      const timeout = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(() => {
            // Silently handle play errors
          });
        }
      }, 200);
      
      return () => clearTimeout(timeout);
    }
  }, [avatarVideoTrack]);
  
  useEffect(() => {
    return () => {
      if (fpsIntervalRef.current) {
        clearInterval(fpsIntervalRef.current);
        fpsIntervalRef.current = null;
      }
      if (room) {
        room.disconnect();
      }
    };
  }, [room]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Video Container - Takes full height minus button space */}
      <div className="flex-1 relative flex items-center justify-center">
        {avatarVideoTrack ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={false}
            className="w-full h-full object-contain"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.play().catch(() => {});
              }
            }}
            onCanPlay={() => {
              if (videoRef.current) {
                videoRef.current.play().catch(() => {});
              }
              if (!avatarReadyNotifiedRef.current && room?.localParticipant) {
                avatarReadyNotifiedRef.current = true;
                try {
                  const msg = new TextEncoder().encode(JSON.stringify({ type: 'avatar_video_ready' }));
                  room.localParticipant.publishData(msg, { reliable: true, topic: 'avatar_status' });
                } catch {}
              }
            }}
            onError={() => {}}
          />
        ) : (
          <div className="text-center">
            {isConnected ? (
              <Loader2 className="w-12 h-12 animate-spin text-white mx-auto" />
            ) : (
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                <Phone className="w-12 h-12 text-gray-600" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dynamic Variable Input Fields */}
      {viewMode === 'dynamic' && dynamicVariableNames.length > 0 && (
        <div className="p-4 bg-gray-900/80 border-t border-gray-800">
          <div className="space-y-2 mb-3">
            {dynamicVariableNames.map((varName) => (
              <div key={varName} className="space-y-1">
                <label className="text-xs font-medium text-gray-400 block">{getVariableLabel(varName)}</label>
                <input
                  type="text"
                  value={dynamicVariableValues[varName] || ''}
                  onChange={(e) => updateVariableValue(varName, e.target.value)}
                  placeholder={`Enter ${getVariableLabel(varName).toLowerCase()}`}
                  disabled={isConnected}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls - Fixed at bottom */}
      <div className="p-4 bg-black border-t border-gray-800">
        {!isConnected ? (
          <button
            onClick={startLiveAvatarSession}
            disabled={isConnecting || !agent?.liveavatar_enabled}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Phone className="w-5 h-5" />
                Start
              </>
            )}
          </button>
        ) : (
          <button
            onClick={stopLiveAvatarSession}
            className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
          >
            <PhoneOff className="w-5 h-5" />
            Stop
          </button>
        )}

        {error && (
          <div className="mt-2 p-2 bg-red-900/20 border border-red-800 rounded-lg">
            <p className="text-red-400 text-xs text-center">{error}</p>
          </div>
        )}
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} autoPlay />
    </div>
  );
};

const EmbedLiveAvatarMinimalPage = ({ agentId }) => {
  return <EmbedLiveAvatarMinimalPageContent agentId={agentId} />;
};

export default EmbedLiveAvatarMinimalPage;

